import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Cloud, Server, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import LeadModal from "@/components/LeadModal";

const PLANS = [
  {
    id: "cloud-hosted",
    tier: "Cloud",
    icon: Cloud,
    name: "Cloud-hosted channel origination",
    price: "$1,250",
    per: "/ channel / month",
    permanent: null,
    features: [
      "Fully managed MOXYPLAY playout in our cloud",
      "Live IP input + playlist integration",
      "6 TB cloud DVR storage (7-day window on Enterprise)",
      "Outputs: HLS · SRT · RTMP · UDP · WebRTC · MPEG-DASH",
      "SCTE-35 insert + SCTE-224 ad decisioning ingest",
      "Signed public XMLTV feed with HMAC token",
      "Multi-region CDN origin (primary + backup, auto-failover)",
      "AI break detection (Gemini 3 Flash) + AI metadata (Claude Sonnet 4.6)",
    ],
    cta: "Book demo",
    ctaMode: "trial",
  },
  {
    id: "hls-multi",
    tier: "Add-on",
    icon: Zap,
    name: "HLS Multi-profile output",
    price: "$120",
    per: "/ channel / month",
    permanent: "$2,000 one-off",
    features: [
      "3 or 5 HLS sub-streams per channel",
      "H.264 + HEVC ABR ladder (800/1600/3200/6000 kbps default)",
      "EPG embedded in every sub-stream",
      "SamsungTV Plus / Roku / Amazon Fire TV / Google TV ready",
      "Multi-language 5.1 audio tracks (LtRt downmix embedded)",
      "Compliance PDF export (ATSC A/85 CALM Act)",
    ],
    cta: "Add to any channel",
    ctaMode: "sales",
  },
  {
    id: "on-prem",
    tier: "On-prem",
    icon: Server,
    name: "On-premises channel origination",
    price: "$350",
    per: "/ channel / month",
    permanent: "$6,200 one-off",
    features: [
      "MOXYPLAY runs on your own hardware (Windows or Linux)",
      "BlackMagic Decklink / AJA Kona SDI ingest + output",
      "NDI 5 + SMPTE 2110 supported (Enterprise adapter)",
      "SCTE-35 hardware insertion",
      "Full 14-layer graphics compositor (GStreamer/NVENC)",
      "Blue/green migration between engine nodes",
      "KMS credential rotation (local Fernet · Vault Transit · AWS KMS)",
    ],
    cta: "Talk to sales",
    ctaMode: "sales",
  },
  {
    id: "enterprise",
    tier: "Enterprise",
    icon: Server,
    name: "Enterprise / white-label",
    price: "Custom",
    per: "annual contract",
    permanent: "Perpetual license option",
    features: [
      "Everything in Cloud + On-prem",
      "Unlimited channels · 7-day DVR window",
      "Vault Transit + AWS KMS wired for credential rotation",
      "Slack / PagerDuty P95 loudness webhooks",
      "Zero-downtime schedule reload during live playout",
      "Per-tenant XMLTV signed feed + branded prospect one-pager",
      "24×7 broadcast NOC + 99.9% egress SLA",
    ],
    cta: "Request proposal",
    ctaMode: "sales",
  },
];

const BOOK_DEMO_URL = "mailto:contact@push2stream.com?subject=MOXYPLAY%20demo%20request";

/**
 * Pricing — public prospect page.
 * `variant="public"` (default): rendered with a nav + full-bleed layout at /pricing.
 * `variant="console"` (legacy): compact grid embedded inside ConsoleLayout.
 */
export default function Pricing({ variant = "public" }) {
  const [leadFor, setLeadFor] = useState(null); // { plan_id, plan_name } | null

  const openLead = (plan) => setLeadFor({ plan_id: plan.id, plan_name: plan.name });

  const grid = (
    <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-3">
      {PLANS.map((p) => (
        <div
          key={p.id}
          data-testid={`pricing-card-${p.id}`}
          className="panel p-5 flex flex-col"
        >
          <div className="flex items-center gap-2 mb-2">
            <p.icon className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
            <span className="font-mono text-[10px] uppercase tracking-widest text-emerald-400">{p.tier}</span>
          </div>
          <div className="font-display font-bold text-lg leading-tight">{p.name}</div>
          <div className="mt-3">
            <span className="font-display font-black text-3xl">{p.price}</span>
            <span className="text-xs text-zinc-500 ml-1">{p.per}</span>
            {p.permanent && (
              <div className="text-[10px] font-mono text-zinc-500 mt-1">or {p.permanent}</div>
            )}
          </div>
          <ul className="mt-4 space-y-1.5 flex-1">
            {p.features.map((f, i) => (
              <li key={i} className="flex items-start gap-1.5 text-xs text-zinc-300">
                <Check className="w-3 h-3 text-emerald-400 shrink-0 mt-0.5" strokeWidth={2} />
                {f}
              </li>
            ))}
          </ul>
          {p.ctaMode === "trial" && variant === "public" ? (
            <a href={BOOK_DEMO_URL}>
              <button
                data-testid={`pricing-cta-${p.id}`}
                className="w-full mt-4 rounded-sm px-3 py-2 tenant-btn font-mono text-[11px] uppercase tracking-widest"
              >
                {p.cta}
              </button>
            </a>
          ) : (
            <button
              data-testid={`pricing-cta-${p.id}`}
              onClick={() => openLead(p)}
              className="mt-4 rounded-sm px-3 py-2 tenant-btn font-mono text-[11px] uppercase tracking-widest"
            >
              {p.cta}
            </button>
          )}
        </div>
      ))}
    </div>
  );

  const bundle = (
    <div className="mt-6 panel p-4">
      <div className="font-display font-bold mb-1">Bundle pricing</div>
      <div className="text-xs text-zinc-400 leading-relaxed">
        <span className="text-emerald-400">3+ channels</span> — 15% off monthly.
        {" · "}
        <span className="text-emerald-400">10+ channels</span> — 25% off + free Multi-region CDN.
        {" · "}
        <span className="text-emerald-400">Annual pre-pay</span> — 2 months free.
        {" · "}
        Every plan includes JWT auth, DVR up to plan cap (trial 15m · starter 1h · pro 24h · enterprise 7d), and the compliance PDF export.
      </div>
    </div>
  );

  const modal = (
    <LeadModal
      open={!!leadFor}
      onClose={() => setLeadFor(null)}
      mode="crm-lead"
      title={leadFor ? `Talk to sales · ${leadFor.plan_name}` : ""}
      subtitle="A MOXYPLAY sales engineer will reply within 1 business day."
      context={{ ...(leadFor || {}), source: "pricing" }}
    />
  );

  if (variant === "console") {
    return (
      <div className="p-4">
        <div className="mb-6">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Pricing</div>
          <h1 className="font-display font-bold text-2xl mt-1">Plans & licensing · June 2026</h1>
          <p className="text-sm text-zinc-500 mt-1">All prices in USD. Contact sales for annual + volume discounts.</p>
        </div>
        {grid}
        {bundle}
        {modal}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-pricing-page">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-40 border-b border-zinc-800/80 bg-[#09090B]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-6">
          <Link to="/" className="flex items-center gap-2" data-testid="pricing-nav-logo">
            <div className="h-8 w-8 rounded-lg overflow-hidden bg-[#060A1F] p-1 flex items-center justify-center">
              <img
                src="https://customer-assets-jt897jd0.emergentagent.net/job_broadcast-cloud-2/artifacts/jeb6uhlp_FullLogo_Transparent_NoBuffer.png"
                alt="MOXYPLAY"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="font-display font-black tracking-tight text-lg">MOXYPLAY</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <Link to="/" className="hover:text-white" data-testid="pricing-nav-home">Home</Link>
            <Link to="/pricing" className="text-white" data-testid="pricing-nav-pricing">Pricing</Link>
            <Link to="/hardware" className="hover:text-white" data-testid="pricing-nav-hardware">Hardware</Link>
            <Link to="/login" className="hover:text-white" data-testid="pricing-nav-login">Sign in</Link>
          </div>
          <a href={BOOK_DEMO_URL} data-testid="pricing-nav-signup">
            <Button className="tenant-btn rounded-sm" size="sm">
              Book demo <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </a>
        </div>
      </nav>

      <section className="pt-32 pb-8 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">§ Pricing · February 2026</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 max-w-3xl">
            Predictable per-channel pricing. Predictable margins.
          </h1>
          <p className="text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            All prices in USD. Every plan includes JWT auth, per-tenant white-label branding, ATSC A/85
            CALM Act compliance PDFs, and the MOXYPLAY 14-layer graphics compositor.
          </p>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto">
          {grid}
          {bundle}

          <div className="mt-10 grid md:grid-cols-2 gap-4">
            <div className="panel p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-2">§ Deploy on your metal</div>
              <div className="font-display font-bold text-xl leading-tight">Need certified hardware?</div>
              <div className="text-sm text-zinc-400 mt-2">
                We ship reference server configurations for 1× 1080p up to 12× 720p with Decklink and NVENC pre-provisioned.
              </div>
              <Link to="/hardware">
                <button className="mt-4 rounded-sm px-3 py-2 tenant-btn font-mono text-[11px] uppercase tracking-widest" data-testid="pricing-see-hardware">
                  See hardware configs →
                </button>
              </Link>
            </div>
            <div className="panel p-5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400 mb-2">§ Migration</div>
              <div className="font-display font-bold text-xl leading-tight">Migrating from Playout Automation / TVU / VMix?</div>
              <div className="text-sm text-zinc-400 mt-2">
                Free scheduler + graphics-layer import. Migration engineer paired for the first 30 days.
              </div>
              <button
                data-testid="pricing-migration-cta"
                onClick={() => setLeadFor({ plan_id: "migration", plan_name: "Migration assistance", source: "pricing-migration" })}
                className="mt-4 rounded-sm px-3 py-2 tenant-btn font-mono text-[11px] uppercase tracking-widest"
              >
                Book a migration call
              </button>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-900 py-8 px-6 text-center text-xs text-zinc-500 font-mono">
        © 2026 MOXYPLAY — a white-label FAST playout platform.
      </footer>

      {modal}
    </div>
  );
}
